import React, { FunctionComponent, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '../Button';
import { ErrorBoundary } from '../ErrorBoundary';
import { ErrorAlert } from '../ErrorAlert';
import { Field, TypedAnswer } from './dynamicFormSchema';
import { DynamicField } from './DynamicField';
import { getValidationRulesForField } from './validators';
import { LoaderSpinner } from '../LoaderSpinner';
import { DynamicFormProps } from './index';
import { DynamicFieldComponent } from './dynamicFields/DynamicFieldComponentBase';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { FieldNumber } from './FieldNumber';
import { Heading } from '../Typography';

export type DynamicFormQuestionField = Field;

interface DynamicFormWithDynamicFieldsProps extends DynamicFormProps {
  dynamicFields: DynamicFieldComponent<Field, TypedAnswer>[];
}

// FormFunctions is a set of functions that are used to control and get information about the top level form.
export type FormFunctions = {
  // isRequiredEnabled returns true if the validation should enforce required validation (e.g. for fields that have
  // `validators: ['required']`)
  isRequiredEnabled: () => boolean;

  // setIsValidationEnabled turns validation on or off for the form.  This is currently used to disable validation when
  // a checkbox with the custom behavior of "disable_form_validation_when_true" is checked.
  setIsValidationEnabled: (enabled: boolean) => void;
};

// DynamicForm display a list of input fields.
export const DynamicFormDisplay: FunctionComponent<DynamicFormWithDynamicFieldsProps> = ({
  dynamicFields,
  showNumbers,
  onSubmit = () => {},
  isSavePending,
  saveError,
  answers,
  showSubmitButton = true,
  disabled = false
}) => {
  const { handleSubmit, control, formState, reset, clearErrors, getValues, watch } = useForm();
  const [isRequiredEnabled, setIsRequiredEnabled] = useState<boolean>(true);

  useEffect(() => {
    if (formState.isDirty) {
      // If the form is dirty, we don't want to overwrite the values with the answers.
      return;
    }

    // Apply all the values from the answers to the form (if they are provided).  This is used to fill out the form
    // for editing or viewing an existing submission.  Because answers and dynamicFields are retrieved in separate
    // requests, we need to have both in the dependency array.  Also, using reset instead of setValue because
    // setValue makes the form dirty and we need to use reset to set the form to clean anyway.
    if (answers) {
      const cleanStateAnswers: FieldValues = {};
      dynamicFields.forEach((dynamicField) => {
        if (dynamicField.providesAnswer()) {
          const answerValue = dynamicField.getAnswer(answers);
          if (answerValue !== undefined) {
            cleanStateAnswers[dynamicField.field.name] = answerValue;
          }
        }
      });
      reset(cleanStateAnswers);
    } else {
      // If no answers are provided, we reset the form to the form to the current values so that the form is not
      // marked as "dirty".
      reset(getValues());
    }
  }, [answers, dynamicFields, reset]);

  useEffect(() => {
    if (!isRequiredEnabled) {
      clearErrors();
    }
  }, [isRequiredEnabled]);

  const formFunctions: FormFunctions = {
    setIsValidationEnabled: (enabled: boolean) => {
      setIsRequiredEnabled(enabled);
    },
    isRequiredEnabled: () => {
      return isRequiredEnabled;
    }
  };

  let fieldNumber = 1;
  return (
    <ErrorBoundary fallback={<ErrorAlert title="Invalid Form" message="Unable to display form elements." />}>
      <form
        onSubmit={handleSubmit((data) => {
          onSubmit(data);
        })}
      >
        {/* TODO: the form is currently locked to a width of 800px. This should be removed once all the dynamic fields
              are resizable (I'm looking at you, ImageDiagnosis) */}
        <div className="flex w-[800px] flex-col gap-8">
          {dynamicFields.map((dynamicField, i) => (
            <div key={`field-${dynamicField.field.name}-${i}`} className="flex flex-row">
              {showNumbers && dynamicField.providesAnswer() ? (
                <FieldNumber
                  number={fieldNumber++}
                  required={isRequiredEnabled && !!dynamicField.field.validators?.includes('required')}
                />
              ) : null}
              <Controller
                control={control}
                name={dynamicField.field.name}
                defaultValue={dynamicField.providesAnswer() ? dynamicField.getDefaultValue() : undefined}
                render={({ field: reactHookField }) => (
                  <DynamicField
                    reactHookField={reactHookField}
                    dynamicFieldComponent={dynamicField}
                    formState={formState}
                    formFunctions={formFunctions}
                    watch={watch}
                  />
                )}
                rules={getValidationRulesForField(dynamicField.field, formFunctions)}
                disabled={isSavePending || disabled}
              />
            </div>
          ))}
          {saveError ? <ErrorAlert title="Error Saving Survey" message={saveError} /> : null}
          {isSavePending ? (
            <div className="flex flex-col items-center rounded-xl border border-primary/40 bg-widget p-4">
              <Heading level="subsection" as="div" className="mb-4">
                Saving Survey
              </Heading>
              <LoaderSpinner size="medium" />
            </div>
          ) : (
            showSubmitButton && (
              <Button name="submit" type="submit">
                Submit
              </Button>
            )
          )}
        </div>
      </form>
    </ErrorBoundary>
  );
};
